import * as Api from '@ViewModels';
import * as React from 'react';
import { toast } from 'sonner';
import { useEventLogging } from '../../../../../../models/Logging';
import { useUserSession } from '../../../../../../models/hooks/appStateHooks';
import { useAIAssistantCreateConversationMutation } from '../../../../../../queries/AI/useAIAssistantCreateConversationMutation';
import { useGetSystemJobsQueries } from '../../../../../../queries/SystemJob';
import { ImpersonationContextViewModel } from '../../../../../../viewmodels/AdminViewModels';
import { isSystemJobFinished } from '../../../../../../queries/SystemJob/jobStatus';
import type { PostBrandingBatch } from '../../../../../components-v2/UnifiedImageDialog/BulkRebrandingTab/hooks/brandingBatch';
import {
	BrandingSystemJobResult,
	IMAGE_BRANDING_ASSISTANT_NAME,
	startImageBrandingStream,
} from '../../../../../components-v2/UnifiedImageDialog/BulkRebrandingTab/hooks/imageBrandingStream';

export type ParallelBrandingSourcePhase = 'idle' | 'streaming' | 'polling' | 'fetching' | 'success' | 'failed';

/** Whether two attachment rows are the same file identity (id per slot). */
export function areAttachmentRowsEqual(
	a: Api.IFileAttachment[] | undefined,
	b: Api.IFileAttachment[] | undefined
): boolean {
	if (!a?.length && !b?.length) {
		return true;
	}
	if (!a || !b || a.length !== b.length) {
		return false;
	}
	return a.every((x, i) => x.id === b[i]?.id);
}

export type TrackedBrandingJob = {
	sourceIds: string[];
	jobId: string;
	runId: number;
};

const DEFAULT_MAX_CONCURRENT_JOBS = 4;

type Params = {
	/** Stable MobX view model; `.toJs()` is only called when issuing requests (avoids unstable hook deps). */
	impersonationContext?: ImpersonationContextViewModel;
	maxConcurrentJobs?: number;
	onBranded: (sourceAttachmentId: string, brandedAttachment: Api.IFileAttachment) => void;
};

/**
 * Creates a dedicated ImageGenerationAgent conversation for multi-post bulk branding in the scheduler.
 * This does not run at the same time as `useApplyBrandingToImages` (UnifiedImageDialog Bulk tab), which is a separate route/modal.
 */
export function useParallelBrandingJobs({
	impersonationContext,
	maxConcurrentJobs = DEFAULT_MAX_CONCURRENT_JOBS,
	onBranded,
}: Params) {
	const logger = useEventLogging('useParallelBrandingJobs');
	const userSession = useUserSession();
	const onBrandedRef = React.useRef(onBranded);
	React.useEffect(() => {
		onBrandedRef.current = onBranded;
	}, [onBranded]);

	const impersonationJs = React.useMemo(() => impersonationContext?.toJs(), [impersonationContext]);

	const [conversationId, setConversationId] = React.useState<string | undefined>(undefined);
	const brandingRunIdRef = React.useRef(0);
	const [trackedJobs, setTrackedJobs] = React.useState<TrackedBrandingJob[]>([]);
	const [sourcePhase, setSourcePhase] = React.useState<Record<string, ParallelBrandingSourcePhase>>({});
	const [isStartingStreams, setIsStartingStreams] = React.useState(false);

	const runAbortRef = React.useRef<AbortController | null>(null);
	const handledJobTerminalRef = React.useRef<Set<string>>(new Set());
	const batchSummaryFiredRef = React.useRef<number | null>(null);

	const pendingBatchesRef = React.useRef<PostBrandingBatch[]>([]);
	const trackedJobsRef = React.useRef<TrackedBrandingJob[]>([]);
	const pumpChainRef = React.useRef<Promise<void>>(Promise.resolve());
	const lastRunBatchesRef = React.useRef<PostBrandingBatch[]>([]);

	const aiAssistantCreateConversationMutation = useAIAssistantCreateConversationMutation({
		onError: error => {
			logger.logApiError('useParallelBrandingJobs-conversation-error', Api.asApiError(error));
			toast.error('Failed to create image assistant conversation');
		},
		onSuccess: data => {
			if (data?.id) {
				setConversationId(data.id);
			}
		},
	});

	const {
		mutate: createAssistantConversation,
		isIdle: createConversationIdle,
		data: createConversationData,
	} = aiAssistantCreateConversationMutation;

	React.useEffect(() => {
		if (conversationId) {
			return;
		}
		if (createConversationIdle && !createConversationData) {
			createAssistantConversation({
				assistantName: IMAGE_BRANDING_ASSISTANT_NAME,
				impersonationContext: impersonationJs,
			});
		}
	}, [conversationId, createAssistantConversation, createConversationData, createConversationIdle, impersonationJs]);

	const systemJobQueries = useGetSystemJobsQueries<BrandingSystemJobResult>({
		jobs: trackedJobs.map(({ jobId, runId }) => ({
			jobId,
			enabled: Boolean(jobId) && runId === brandingRunIdRef.current,
		})),
	});

	const schedulePump = React.useCallback(
		(expectedRunId: number): Promise<void> => {
			const run = async () => {
				if (!conversationId) {
					return;
				}
				const ac = runAbortRef.current;
				if (!ac || ac.signal.aborted) {
					return;
				}
				while (
					pendingBatchesRef.current.length > 0 &&
					trackedJobsRef.current.length < maxConcurrentJobs &&
					!ac.signal.aborted &&
					brandingRunIdRef.current === expectedRunId
				) {
					const batch = pendingBatchesRef.current.shift()!;
					try {
						const jobId = await startImageBrandingStream({
							userSession,
							conversationId,
							impersonationContext: impersonationJs,
							attachmentIds: batch.attachmentIds,
							templateId: batch.templateId,
							signal: ac.signal,
						});
						if (ac.signal.aborted || brandingRunIdRef.current !== expectedRunId) {
							break;
						}
						const entry: TrackedBrandingJob = {
							sourceIds: batch.attachmentIds,
							jobId,
							runId: expectedRunId,
						};
						setTrackedJobs(prev => {
							const next = [...prev, entry];
							trackedJobsRef.current = next;
							return next;
						});
						setSourcePhase(prev => {
							const next = { ...prev };
							for (const id of batch.attachmentIds) {
								next[id] = 'polling';
							}
							return next;
						});
					} catch (e) {
						if (ac.signal.aborted || brandingRunIdRef.current !== expectedRunId) {
							break;
						}
						logger.logApiError(`parallel-branding-stream-batch`, Api.asApiError(e));
						toast.error((e as any)?.message || 'Failed to start branding stream');
						setSourcePhase(prev => {
							const next = { ...prev };
							for (const id of batch.attachmentIds) {
								next[id] = 'failed';
							}
							return next;
						});
					}
				}
			};

			const p = pumpChainRef.current.catch((): undefined => undefined).then(run);
			pumpChainRef.current = p;
			return p;
		},
		[conversationId, impersonationJs, logger, maxConcurrentJobs, userSession]
	);

	const startBranding = React.useCallback(
		(batches: PostBrandingBatch[]) => {
			const normalized = batches
				.map(b => ({
					...b,
					attachmentIds: Array.from(new Set(b.attachmentIds.filter(Boolean))),
				}))
				.filter(b => b.attachmentIds.length > 0);
			if (normalized.length === 0) {
				return;
			}
			if (!conversationId) {
				toast.error('Image assistant is not ready yet. Try again in a moment.');
				return;
			}

			runAbortRef.current?.abort();
			const ac = new AbortController();
			runAbortRef.current = ac;

			++brandingRunIdRef.current;
			const runIdForSession = brandingRunIdRef.current;
			trackedJobsRef.current = [];
			setTrackedJobs([]);
			handledJobTerminalRef.current.clear();
			batchSummaryFiredRef.current = null;

			lastRunBatchesRef.current = normalized.map(b => ({ ...b, attachmentIds: [...b.attachmentIds] }));
			pendingBatchesRef.current = normalized;

			const allIds = normalized.flatMap(b => b.attachmentIds);
			setSourcePhase(() => {
				const next: Record<string, ParallelBrandingSourcePhase> = {};
				for (const id of allIds) {
					next[id] = 'streaming';
				}
				return next;
			});

			setIsStartingStreams(true);
			pumpChainRef.current = Promise.resolve();
			void schedulePump(runIdForSession).finally(() => {
				if (brandingRunIdRef.current === runIdForSession) {
					setIsStartingStreams(false);
				}
			});
		},
		[conversationId, schedulePump]
	);

	const retryFailed = React.useCallback(() => {
		const failedIds = new Set(
			Object.entries(sourcePhase)
				.filter(([, phase]) => phase === 'failed')
				.map(([id]) => id)
		);
		if (failedIds.size === 0) {
			return;
		}
		const retryBatches: PostBrandingBatch[] = lastRunBatchesRef.current
			.map(b => ({
				...b,
				attachmentIds: b.attachmentIds.filter(id => failedIds.has(id)),
			}))
			.filter(b => b.attachmentIds.length > 0);
		if (retryBatches.length === 0) {
			return;
		}
		startBranding(retryBatches);
	}, [sourcePhase, startBranding]);

	React.useEffect(() => {
		if (!trackedJobs.length) {
			return;
		}
		trackedJobs.forEach((entry, index) => {
			if (entry.runId !== brandingRunIdRef.current) {
				return;
			}
			if (handledJobTerminalRef.current.has(entry.jobId)) {
				return;
			}
			const q = systemJobQueries[index];
			if (!q || q.isLoading) {
				return;
			}

			const finalizeJobFailure = (message?: string) => {
				if (brandingRunIdRef.current !== entry.runId) {
					return;
				}
				handledJobTerminalRef.current.add(entry.jobId);
				setSourcePhase(prev => {
					const next = { ...prev };
					for (const id of entry.sourceIds) {
						next[id] = 'failed';
					}
					return next;
				});
				setTrackedJobs(prev => {
					const next = prev.filter(t => t.jobId !== entry.jobId);
					trackedJobsRef.current = next;
					return next;
				});
				toast.error(message ?? 'Branding job failed for one post. Use Retry failed.');
				void schedulePump(brandingRunIdRef.current);
			};

			if (q.isError) {
				finalizeJobFailure();
				return;
			}

			if (!q.data) {
				return;
			}
			if (!isSystemJobFinished(q.data)) {
				return;
			}

			const rows = q.data.resultImages;
			if (!rows?.length) {
				if (q.isFetching) {
					return;
				}
				finalizeJobFailure();
				return;
			}

			const rowsBySource = new Map(rows.map(r => [r.sourceFileAttachmentId, r]));
			const missingOutput = entry.sourceIds.filter(id => !rowsBySource.get(id)?.fileAttachmentId);
			if (missingOutput.length > 0) {
				for (const id of missingOutput) {
					setSourcePhase(prev => ({ ...prev, [id]: 'failed' }));
				}
				toast.error('Branding job returned incomplete results for some images.');
			}

			const toFetch = entry.sourceIds.filter(id => rowsBySource.get(id)?.fileAttachmentId);
			if (toFetch.length === 0) {
				handledJobTerminalRef.current.add(entry.jobId);
				setTrackedJobs(prev => {
					const next = prev.filter(t => t.jobId !== entry.jobId);
					trackedJobsRef.current = next;
					return next;
				});
				void schedulePump(brandingRunIdRef.current);
				return;
			}

			handledJobTerminalRef.current.add(entry.jobId);
			for (const id of toFetch) {
				setSourcePhase(prev => ({ ...prev, [id]: 'fetching' }));
			}

			const runIdForFetch = entry.runId;
			void (async () => {
				try {
					await Promise.all(
						toFetch.map(async sourceId => {
							const row = rowsBySource.get(sourceId)!;
							try {
								const branded = await userSession.webServiceHelper.callAsync<Api.IFileAttachment>(
									Api.ImpersonationBroker.composeApiUrl({
										impersonationContext: impersonationJs,
										urlPath: `attachment/${row.fileAttachmentId}`,
									}),
									'GET'
								);
								if (brandingRunIdRef.current === runIdForFetch) {
									onBrandedRef.current(sourceId, branded);
									setSourcePhase(prev => ({ ...prev, [sourceId]: 'success' }));
								}
							} catch (e) {
								logger.logApiError(`parallel-branding-fetch-${sourceId}`, Api.asApiError(e));
								if (brandingRunIdRef.current === runIdForFetch) {
									setSourcePhase(prev => ({ ...prev, [sourceId]: 'failed' }));
									toast.error('Failed to load a branded image.');
								}
							}
						})
					);
				} finally {
					if (brandingRunIdRef.current === runIdForFetch) {
						setTrackedJobs(prev => {
							const next = prev.filter(t => t.jobId !== entry.jobId);
							trackedJobsRef.current = next;
							return next;
						});
						void schedulePump(brandingRunIdRef.current);
					}
				}
			})();
		});
	}, [impersonationJs, logger, schedulePump, systemJobQueries, trackedJobs, userSession]);

	const idsInFlightOrPending = React.useMemo(() => {
		return Object.entries(sourcePhase).some(([, p]) => ['streaming', 'polling', 'fetching'].includes(p));
	}, [sourcePhase]);

	const failedCount = React.useMemo(() => Object.values(sourcePhase).filter(p => p === 'failed').length, [sourcePhase]);

	const successCount = React.useMemo(
		() => Object.values(sourcePhase).filter(p => p === 'success').length,
		[sourcePhase]
	);

	React.useEffect(() => {
		if (isStartingStreams || idsInFlightOrPending || trackedJobs.length > 0) {
			return;
		}
		const phases = Object.values(sourcePhase);
		if (phases.length === 0) {
			return;
		}
		const allTerminal = phases.every(p => p === 'success' || p === 'failed');
		if (!allTerminal) {
			return;
		}
		if (batchSummaryFiredRef.current === brandingRunIdRef.current) {
			return;
		}
		batchSummaryFiredRef.current = brandingRunIdRef.current;
		const ok = Object.values(sourcePhase).filter(p => p === 'success').length;
		const bad = Object.values(sourcePhase).filter(p => p === 'failed').length;
		if (bad === 0) {
			toast.success(ok > 0 ? `Branding applied to ${ok} image(s).` : 'Branding finished.');
		} else if (ok === 0) {
			toast.error('Branding did not complete. Retry failed images.');
		} else {
			toast.warning(`Branding finished: ${ok} succeeded, ${bad} failed. You can retry failed images.`);
		}
	}, [idsInFlightOrPending, isStartingStreams, sourcePhase, trackedJobs.length]);

	React.useEffect(() => {
		return () => {
			runAbortRef.current?.abort();
		};
	}, []);

	const conversationReady = Boolean(conversationId);

	return {
		startBranding,
		retryFailed,
		conversationReady,
		isBusy: isStartingStreams || idsInFlightOrPending,
		failedCount,
		successCount,
		sourcePhase,
	};
}
